/**
 * CRM Multi-Compagnia · Dashboard avanzata (Fase 4)
 *
 * Layout:
 *  - Header con filtri unificati (compagnia, ramo, subagente, date range, stato)
 *  - Riga 1: 6 KPI card (premi YTD+delta, polizze attive+delta, clienti, scadenze 30gg + €, insoluti + €, provvigioni YTD)
 *  - Riga 2: Bar Produzione/Compagnia + Donut Scadenze/Ramo
 *  - Riga 3: Line Incassi 12 mesi (3 serie) full width
 *  - Riga 4: Tabella Confronto Compagnie + Card Cross-Selling
 *  - Riga 5: Pie mono/multi + Bar top prodotti + Bar subagenti
 *  - Riga 6: Heatmap mese × ramo full width
 *  - Riga 7: Line 24 mesi portafoglio multi-serie full width
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import {
  Building2, FileText, Users, Banknote, CalendarClock, AlertTriangle,
  Wallet, Database, RefreshCw, ArrowRight, Car,
} from "lucide-react";
import axios from "axios";
import {
  api, useApi, useDebounced, Spinner, Btn, fmtEUR, fmtDate, RAMI_LABEL,
  CompanyAvatar, RamoChip, StatusBadge, toast,
} from "@/components/crm-multi/shared";
import FiltersBar, { readQS } from "@/components/crm-multi/dashboard/FiltersBar";
import KpiCard from "@/components/crm-multi/dashboard/KpiCard";
import ChartContainer from "@/components/crm-multi/dashboard/ChartContainer";
import CrossSellingCard from "@/components/crm-multi/dashboard/CrossSellingCard";
import Heatmap from "@/components/crm-multi/dashboard/Heatmap";

const COLORS = ["#059669", "#2563eb", "#7c3aed", "#d97706", "#dc2626", "#0891b2", "#db2777", "#ea580c"];

// Navigazione helper
const goto = (path) => { window.location.assign(path); };

export default function CrmMultiDashboardPage() {
  const [filters, setFilters] = useState(readQS());
  const debounced = useDebounced(filters, 300);
  const [sortField, setSortField] = useState("premi_ytd");
  const [sortDir, setSortDir] = useState("desc");

  // FASE 4.2.11 — Mini-cruscotto NLT (count Ayvens/Arval)
  const [nltCounts, setNltCounts] = useState({ ayvens: null, arval: null, total: null, loading: true });
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const resp = await fetch(`${apiBase}/noleggio/catalog?limit=1`);
        const d = await resp.json();
        if (abort) return;
        const sc = d?.sources_count || d?.sources || {};
        setNltCounts({
          ayvens: sc.ayvens ?? 0,
          arval: sc.arval ?? 0,
          total: d?.total ?? ((sc.ayvens || 0) + (sc.arval || 0)),
          loading: false,
        });
      } catch (_) { setNltCounts(c => ({ ...c, loading: false })); }
    })();
    return () => { abort = true; };
  }, []);

  const params = useMemo(() => {
    const out = {};
    Object.entries(debounced).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [debounced]);

  const ANALYTICS = `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/multi/analytics`;
  const fetchAn = useCallback((path, extra = {}) =>
    axios.get(`${ANALYTICS}${path}`, { params: { ...params, ...extra }, withCredentials: true }).then(r => r.data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ANALYTICS, params]);

  const summary = useApi(() => fetchAn("/summary"), [params]);
  const produzione = useApi(() => fetchAn("/produzione-per-compagnia"), [params]);
  const scadenzeRamo = useApi(() => fetchAn("/scadenze-per-ramo"), [params]);
  const incassi = useApi(() => fetchAn("/incassi-mensili", { mesi: 12 }), [params]);
  const provvigioni = useApi(() => fetchAn("/provvigioni-stimate"), [params]);
  const monoMulti = useApi(() => fetchAn("/clienti-mono-multi"), [params]);
  const crossSell = useApi(() => fetchAn("/cross-selling", { top: 10 }), [params]);
  const trend = useApi(() => fetchAn("/portafoglio-trend", { mesi: 24 }), [params]);
  const confronto = useApi(() => fetchAn("/confronto-compagnie"), [params]);
  const topProd = useApi(() => fetchAn("/top-prodotti", { top: 10 }), [params]);
  const subagenti = useApi(() => fetchAn("/subagenti-performance"), [params]);
  const heatmap = useApi(() => fetchAn("/heatmap-scadenze", { mesi: 12 }), [params]);

  const refreshAll = () => {
    summary.refresh(); produzione.refresh(); scadenzeRamo.refresh();
    incassi.refresh(); provvigioni.refresh(); monoMulti.refresh();
    crossSell.refresh(); trend.refresh(); confronto.refresh();
    topProd.refresh(); subagenti.refresh(); heatmap.refresh();
  };

  const onExportCsv = () => {
    const rows = (confronto.data?.items || []).map(r => ({
      compagnia: r.compagnia,
      premi_ytd: r.premi_ytd,
      polizze: r.polizze,
      premio_medio: r.premio_medio,
      growth_pct: r.growth_pct,
      sinistri_aperti: r.sinistri_aperti,
    }));
    if (rows.length === 0) { toast("Nessun dato da esportare", "info"); return; }
    const head = Object.keys(rows[0]).join(",");
    const body = rows.map(r => Object.values(r).map(v => v == null ? "" : String(v).replace(/,/g, ";")).join(",")).join("\n");
    const csv = `${head}\n${body}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dashboard-confronto-compagnie-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast("CSV esportato", "success");
  };

  // Seed demo (riusato)
  const [seedingLoading, setSeedingLoading] = useState(false);
  const onSeed = async () => {
    setSeedingLoading(true);
    try {
      const r = await api.post("/seed-demo");
      toast(r.stats?.skipped ? "Seed: già presente" : "Seed completato", "success");
      refreshAll();
    } catch (e) { toast("Errore seed", "error"); }
    finally { setSeedingLoading(false); }
  };

  const k = summary.data || {};
  const confrontoSorted = useMemo(() => {
    const items = [...(confronto.data?.items || [])];
    items.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return items;
  }, [confronto.data, sortField, sortDir]);

  const toggleSort = (f) => {
    if (sortField === f) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(f); setSortDir("desc"); }
  };

  return (
    <div className="space-y-4" data-testid="cm-dashboard">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-zinc-900">Dashboard Multi-Compagnia</h2>
          <p className="text-sm text-zinc-500">Analisi portafoglio · KPI · grafici · cross-selling</p>
        </div>
        <div className="flex gap-2">
          <Btn variant="outline" size="sm" onClick={onSeed} disabled={seedingLoading} data-testid="cm-seed-btn">
            <Database className="w-4 h-4" /> Seed Demo
          </Btn>
          <Btn variant="outline" size="sm" onClick={refreshAll} data-testid="cm-refresh-btn">
            <RefreshCw className="w-4 h-4" /> Aggiorna
          </Btn>
        </div>
      </header>

      <FiltersBar value={filters} onChange={setFilters} onExportCsv={onExportCsv} />

      {/* FASE 4.2.11 — Mini-cruscotto Noleggio NLT */}
      <div className="bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid="cm-nlt-widget">
        <div className="flex items-center gap-2">
          <Car className="w-5 h-5 text-emerald-700" />
          <span className="text-sm font-semibold text-zinc-900">Noleggio NLT</span>
        </div>
        {nltCounts.loading ? (
          <span className="text-xs text-zinc-500">Caricamento…</span>
        ) : (
          <>
            <span className="text-xs text-zinc-700">Catalogo: <strong className="text-zinc-900">{nltCounts.total ?? 0}</strong> veicoli</span>
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-800" data-testid="cm-nlt-ayvens">
              Ayvens · <strong>{nltCounts.ayvens ?? 0}</strong>
            </span>
            <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-white border border-blue-200 text-blue-800" data-testid="cm-nlt-arval">
              Arval · <strong>{nltCounts.arval ?? 0}</strong>
            </span>
            <a href="/noleggio" className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-semibold inline-flex items-center gap-1" data-testid="cm-nlt-link">
              Apri catalogo <ArrowRight className="w-3 h-3" />
            </a>
            <a href="/admin/compliance" className="text-xs text-emerald-700 hover:text-emerald-900 font-semibold inline-flex items-center gap-1 ml-2" data-testid="cm-compliance-link">
              🛡️ Compliance Hub
            </a>
          </>
        )}
      </div>

      {/* Riga 1: 6 KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Banknote} label="Premi YTD" value={fmtEUR(k.premi_totali_ytd)}
          subValue={`Mese: ${fmtEUR(k.premi_mese_corrente)}`}
          deltaPct={k.delta_premi_ytd_pct} color="emerald" testid="cm-kpi-premi" />
        <KpiCard icon={FileText} label="Polizze attive" value={k.polizze_attive ?? 0}
          subValue={`Su ${k.polizze_totali ?? 0} totali`}
          color="blue" testid="cm-kpi-polizze"
          onClick={() => goto(filters.company_id ? `/admin?tab=cm_polizze&company_id=${filters.company_id}` : "/admin?tab=cm_polizze")} />
        <KpiCard icon={Users} label="Clienti" value={k.clienti_totali ?? 0}
          color="violet" testid="cm-kpi-clienti"
          onClick={() => goto("/admin?tab=cm_clienti")} />
        <KpiCard icon={CalendarClock} label="Scadenze 30gg" value={k.scadenze_30gg ?? 0}
          subValue={fmtEUR(k.importo_scadenze_30gg)}
          color="orange" testid="cm-kpi-scadenze"
          onClick={() => goto("/admin?tab=cm_scadenze")} />
        <KpiCard icon={AlertTriangle} label="Insoluti aperti" value={k.insoluti_aperti_count ?? 0}
          subValue={fmtEUR(k.insoluti_aperti_importo)}
          color="rose" testid="cm-kpi-insoluti"
          onClick={() => goto("/admin?tab=cm_insoluti")} />
        <KpiCard icon={Wallet} label="Provvigioni YTD" value={fmtEUR(k.provvigioni_stimate_ytd)}
          subValue="Stima da prodotti"
          color="amber" testid="cm-kpi-provvigioni" />
      </div>

      {/* Riga 2: Produzione + Scadenze ramo */}
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartContainer title="Produzione per Compagnia"
          subtitle="Premio annuo polizze attive"
          loading={produzione.loading} error={produzione.error}
          isEmpty={(produzione.data?.items || []).length === 0}
          testid="cm-chart-produzione">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={produzione.data?.items || []} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="compagnia" width={120} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="premio_totale" fill="#059669" radius={[0, 4, 4, 0]} cursor="pointer"
                onClick={(d) => d?.company_id && goto(`/admin?tab=cm_polizze&company_id=${d.company_id}`)} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Scadenze per Ramo (90gg)"
          subtitle="Polizze in scadenza"
          loading={scadenzeRamo.loading} error={scadenzeRamo.error}
          isEmpty={(scadenzeRamo.data?.items || []).length === 0}
          testid="cm-chart-scadenze-ramo">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={(scadenzeRamo.data?.items || []).map(x => ({ name: RAMI_LABEL[x.ramo] || x.ramo, value: x.count, ramo: x.ramo }))}
                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                label={(e) => e.name + ": " + e.value}
                onClick={(d) => d?.ramo && goto(`/admin?tab=cm_scadenze&ramo=${d.ramo}`)}>
                {(scadenzeRamo.data?.items || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} cursor="pointer" />)}
              </Pie>
              <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      {/* Riga 3: Incassi 12 mesi full width */}
      <ChartContainer title="Incassi · Scadenze · Insoluti (ultimi 12 mesi)"
        subtitle="Andamento mensile in €"
        loading={incassi.loading} error={incassi.error}
        isEmpty={(incassi.data?.items || []).every(x => x.incassi === 0 && x.scadenze === 0 && x.insoluti === 0)}
        testid="cm-chart-incassi-mensili">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={incassi.data?.items || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtEUR(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="incassi" stroke="#059669" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="scadenze" stroke="#d97706" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="insoluti" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>

      {/* Riga 4: Confronto + Cross-selling */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <ChartContainer title="Confronto Compagnie"
            subtitle="Premi · polizze · sinistri ratio · crescita YoY"
            loading={confronto.loading} error={confronto.error}
            isEmpty={confrontoSorted.length === 0}
            testid="cm-chart-confronto-compagnie">
            <div className="overflow-x-auto -mx-4">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-zinc-500 border-b border-zinc-100">
                  <tr>
                    <th className="text-left px-4 py-2">Compagnia</th>
                    <SortableTh field="premi_ytd" label="Premi YTD" sortField={sortField} sortDir={sortDir} onToggle={toggleSort} align="right" />
                    <SortableTh field="polizze" label="Polizze" sortField={sortField} sortDir={sortDir} onToggle={toggleSort} align="right" />
                    <SortableTh field="premio_medio" label="Premio medio" sortField={sortField} sortDir={sortDir} onToggle={toggleSort} align="right" />
                    <SortableTh field="growth_pct" label="Crescita YoY" sortField={sortField} sortDir={sortDir} onToggle={toggleSort} align="right" />
                    <SortableTh field="sinistri_aperti" label="Sinistri" sortField={sortField} sortDir={sortDir} onToggle={toggleSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {confrontoSorted.map((r, i) => (
                    <tr key={r.company_id} className="border-b border-zinc-50 hover:bg-emerald-50/30 cursor-pointer"
                      onClick={() => goto(`/admin?tab=cm_polizze&company_id=${r.company_id}`)}
                      data-testid={`cm-confronto-row-${i}`}>
                      <td className="px-4 py-2 flex items-center gap-2">
                        <CompanyAvatar nome={r.compagnia} size="sm" />
                        <span className="font-medium text-zinc-900">{r.compagnia}</span>
                      </td>
                      <td className="px-2 text-right font-mono">{fmtEUR(r.premi_ytd)}</td>
                      <td className="px-2 text-right">{r.polizze}</td>
                      <td className="px-2 text-right font-mono text-zinc-700">{fmtEUR(r.premio_medio)}</td>
                      <td className={`px-2 text-right font-mono ${r.growth_pct == null ? "text-zinc-400" : r.growth_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {r.growth_pct == null ? "—" : `${r.growth_pct > 0 ? "+" : ""}${r.growth_pct}%`}
                      </td>
                      <td className="px-2 text-right">{r.sinistri_aperti}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ChartContainer>
        </div>
        <CrossSellingCard items={crossSell.data?.items} loading={crossSell.loading}
          error={crossSell.error} onCreated={crossSell.refresh} />
      </div>

      {/* Riga 5: Mono/Multi + Top prodotti + Subagenti */}
      <div className="grid md:grid-cols-3 gap-4">
        <ChartContainer title="Clienti mono vs multi-compagnia"
          loading={monoMulti.loading} error={monoMulti.error}
          isEmpty={(monoMulti.data?.items || []).every(x => x.count === 0)}
          testid="cm-chart-monomulti">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={(monoMulti.data?.items || []).map(x => ({ name: x.segmento, value: x.count }))}
                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                {(monoMulti.data?.items || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Top 10 prodotti per premio"
          loading={topProd.loading} error={topProd.error}
          isEmpty={(topProd.data?.items || []).length === 0}
          testid="cm-chart-top-prodotti">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topProd.data?.items || []} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="prodotto" width={110} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="premi" fill="#2563eb" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer title="Subagenti — Premi YTD"
          loading={subagenti.loading} error={subagenti.error}
          isEmpty={(subagenti.data?.items || []).length === 0}
          testid="cm-chart-subagenti">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={subagenti.data?.items || []} margin={{ left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="nome" tick={{ fontSize: 9 }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="premi_ytd" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      {/* Riga 6: Heatmap mese × ramo */}
      <ChartContainer title="Heatmap scadenze · mese × ramo (12 mesi)"
        subtitle="Volume polizze in scadenza, click per drill-down"
        loading={heatmap.loading} error={heatmap.error}
        isEmpty={!heatmap.data?.cells || heatmap.data.cells.every(c => c.count === 0)}
        testid="cm-chart-heatmap">
        <Heatmap data={heatmap.data}
          onCellClick={(c) => goto(`/admin?tab=cm_scadenze&ramo=${c.ramo}&mese=${c.mese}`)} />
      </ChartContainer>

      {/* Riga 7: Trend portafoglio 24 mesi */}
      <ChartContainer title="Trend portafoglio 24 mesi"
        subtitle="Polizze attive per compagnia (cumulativo mensile)"
        loading={trend.loading} error={trend.error}
        isEmpty={(trend.data?.items || []).every(x => Object.values(x).filter(v => typeof v === "number").every(v => v === 0))}
        testid="cm-chart-trend">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trend.data?.items || []}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {(trend.data?.compagnie || []).map((c, i) => (
              <Line key={c} type="monotone" dataKey={c} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 1.5 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}

function SortableTh({ field, label, sortField, sortDir, onToggle, align = "left" }) {
  const active = sortField === field;
  return (
    <th className={`px-2 py-2 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none`}
      onClick={() => onToggle(field)} data-testid={`cm-confronto-sort-${field}`}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-emerald-600">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}
